/**
 * AIF-03 — TanStack Query hooks for the lyse/v1 ai-findings-import surface
 * (4 ops under `/data-acquisitions/{id}/ai-findings-import`): analyze (dry-run),
 * start (async job), poll status, cancel.
 *
 * The host `useApi()` axios instance is base-pinned to `/api/lyse/v1`, so these
 * paths resolve against the lyse gateway directly (unlike the taxonomy
 * ai-models hooks, which override the baseURL). Analyze is a mutation (it takes
 * the file payload + tunable scope/floor and is re-run on changes); the poll is
 * a `useQuery` with a fixed refetch interval that stops once the job is
 * terminal.
 *
 * SYNC: lyse/v1@7.16.0 (ai-findings-import tag).
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type {
  AnalyzeAiFindingsImportBody,
  AnalyzeAiFindingsImportReport,
  AiFindingsImportJobStatus,
  GetDataAcquisitionsParams,
  GetDataAcquisitionsResponse,
  ImportAiFindingsBody,
  StartAiFindingsImportResponse,
} from "@aeriosiq/types";

const enc = encodeURIComponent;

/** Poll cadence for the import job (story step 4: ~750ms). */
export const IMPORT_POLL_INTERVAL_MS = 750;

// ── Step 1: DA picker search ─────────────────────────────────────────────────

export const importDaKeys = {
  search: (params: GetDataAcquisitionsParams) =>
    ["ai-findings-import", "da-search", params] as const,
};

/**
 * GET /data-acquisitions — the AIF-02 picker query. Always state-restricted to
 * PRE_SCREENING/EXPERT_ANALYSIS (the only importable states) and leaves
 * `include_completed` at the BE default (false). Reuses the canonical
 * `getDataAcquisitions` op + its 7.15.0 display fields.
 */
export function useImportDaSearch(q: string) {
  const api = useApi();
  const params: GetDataAcquisitionsParams = {
    q: q.trim() || undefined,
    state: ["PRE_SCREENING", "EXPERT_ANALYSIS"],
    page: 1,
    pageSize: 25,
  };
  return useQuery<GetDataAcquisitionsResponse>({
    queryKey: importDaKeys.search(params),
    queryFn: async () => {
      const res = await api.get<GetDataAcquisitionsResponse>(
        "/data-acquisitions",
        {
          params: {
            q: params.q,
            state: params.state,
            page: params.page,
            pageSize: params.pageSize,
          },
          paramsSerializer: { indexes: null }, // repeat `state=X&state=Y`
        },
      );
      return res.data;
    },
    placeholderData: (prev) => prev,
    staleTime: 30_000,
    retry: 1,
    meta: { failureToast: "Data acquisitions unavailable" },
  });
}

// ── Step 3: analyze (dry-run) ────────────────────────────────────────────────

/** POST …/ai-findings-import/analyze — no writes; drives the overview. */
export function useAnalyzeImportMutation(dataAcquisitionId: string) {
  const api = useApi();
  return useMutation<
    AnalyzeAiFindingsImportReport,
    Error,
    AnalyzeAiFindingsImportBody
  >({
    mutationFn: async (body) => {
      const res = await api.post<AnalyzeAiFindingsImportReport>(
        `/data-acquisitions/${enc(dataAcquisitionId)}/ai-findings-import/analyze`,
        body,
      );
      return res.data;
    },
  });
}

// ── Step 4: start / poll / cancel ────────────────────────────────────────────

/** POST …/ai-findings-import — starts the async job (202 → importId). */
export function useStartImportMutation(dataAcquisitionId: string) {
  const api = useApi();
  return useMutation<
    StartAiFindingsImportResponse,
    Error,
    ImportAiFindingsBody
  >({
    mutationFn: async (body) => {
      const res = await api.post<StartAiFindingsImportResponse>(
        `/data-acquisitions/${enc(dataAcquisitionId)}/ai-findings-import`,
        body,
      );
      return res.data;
    },
  });
}

/**
 * GET …/ai-findings-import/{importId} — polls every ~750ms while the job is
 * running, stops once terminal (done/cancelled/failed). `enabled` lets the
 * caller hold off until an importId exists.
 */
export function useImportJobStatus(
  dataAcquisitionId: string,
  importId: string | null,
) {
  const api = useApi();
  return useQuery<AiFindingsImportJobStatus>({
    queryKey: ["ai-findings-import", "job", dataAcquisitionId, importId],
    enabled: importId !== null,
    queryFn: async () => {
      const res = await api.get<AiFindingsImportJobStatus>(
        `/data-acquisitions/${enc(dataAcquisitionId)}/ai-findings-import/${enc(
          importId ?? "",
        )}`,
      );
      return res.data;
    },
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === undefined || status === "running") {
        return IMPORT_POLL_INTERVAL_MS;
      }
      return false; // terminal — stop polling
    },
    refetchIntervalInBackground: false,
  });
}

/** POST …/ai-findings-import/{importId}/cancel — idempotent (202). */
export function useCancelImportMutation(dataAcquisitionId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: async (importId: string): Promise<void> => {
      await api.post(
        `/data-acquisitions/${enc(dataAcquisitionId)}/ai-findings-import/${enc(
          importId,
        )}/cancel`,
      );
    },
  });
}
